/* eslint-disable consistent-return */
import React, { useRef, useEffect, useState } from 'react';
import useIsMobile from '@/hooks/useIsMobile';
import { handleImageUrl } from '$/utils/image';
import styles from './index.module.scss';

interface FeatureItem {
  id: string;
  title: string;
  description: string;
}

interface FeatureSection {
  id: string;
  title: string;
  features: FeatureItem[];
  imagePosition: 'left' | 'right';
  imageSrc?: string;
  videoWebm?: string;
  videoMp4?: string;
  videoTs?: string;
  poster: string;
}

const featureSections: FeatureSection[] = [
  {
    id: 'section-1',
    title: 'One-Sentence Ad Creation',
    features: [
      {
        id: 's1-f1',
        title: 'Intelligent campaign planning',
        description: 'Your AI creative partner. It understands your objective, maps out the visual strategy, and recommends layouts and design elements aligned with conversion.',
      },
      {
        id: 's1-f2',
        title: 'High-ROAS ad creatives',
        description: 'Built for performance across TikTok, Meta, and YouTube. We craft scroll-stopping posters and visuals engineered to lift CTR and maximize ROI.',
      },
      {
        id: 's1-f3',
        title: 'One-click HD enhancement',
        description: 'Instantly upgrade UGC into high-impact, platform-ready creatives optimized for TikTok, Instagram, and YouTube.',
      },
    ],
    imagePosition: 'right',
    videoWebm: 'https://roboneo-public.meitudata.com/public/roboneo-web-assets/final1_sfx_820.webm',
    videoMp4: 'https://roboneo-public.meitudata.com/public/roboneo-web-assets/final1_sfx_820.mp4',
    videoTs: 'https://titan-h5.meitu.com/roboneo/assets/overseahome-feature-video1.ts',
    poster: 'https://roboneo-public.meitudata.com/public/roboneo-web-assets/final1_sfx_820_poster.jpg',
  },
  {
    id: 'section-2',
    title: 'Short Drama Creation',
    features: [
      {
        id: 's2-f1',
        title: 'Edit by describing your vision',
        description: 'Tell RoboNeo what you want. It understands context, isolates subjects, optimizes lighting and color, applies cinematic styling, and delivers a production-ready visual — automatically.',
      },
      {
        id: 's2-f2',
        title: 'Intelligent video processing',
        description: 'RoboNeo analyzes every frame, detects and tracks objects, applies precise masking, ensures visual consistency, and exports a polished final cut — no manual timeline editing required.',
      },
      {
        id: 's2-f3',
        title: 'End-to-end creation through conversation',
        description: 'Simply describe your goal. The AI agent plans the workflow, selects the right tools, generates scenes, refines transitions, adds music, and produces a complete video from start to finish.',
      },
    ],
    imagePosition: 'left',
    videoWebm: 'https://roboneo-public.meitudata.com/public/roboneo-web-assets/final2_sfx_820.webm',
    videoMp4: 'https://roboneo-public.meitudata.com/public/roboneo-web-assets/final2_sfx_820.mp4',
    videoTs: 'https://titan-h5.meitu.com/roboneo/assets/overseahome-feature-video2.ts',
    poster: 'https://roboneo-public.meitudata.com/public/roboneo-web-assets/final2_sfx_820_poster.jpg',
  },
  {
    id: 'section-3',
    title: 'E-commerce Ad Creation',
    features: [
      {
        id: 's3-f1',
        title: 'Account-specific viral strategy',
        description: 'RoboNeo analyzes your brand positioning, creative style, and audience signals to recommend viral directions that align with your account — not recycled trends.',
      },
      {
        id: 's3-f2',
        title: 'Viral breakdowns, not just output',
        description: 'Beyond content generation, the Agent deconstructs winning creatives — structure, pacing, emotional hooks, and distribution logic — so you understand the mechanics behind performance.',
      },
      {
        id: 's3-f3',
        title: 'Turn virality into repeatable assets',
        description: 'High-performing structures, narrative frameworks, rhythm patterns, and visual systems are captured and converted into reusable creative assets — enabling scalable, long-term growth.',
      },
    ],
    imagePosition: 'right',
    videoWebm: 'https://roboneo-public.meitudata.com/public/roboneo-web-assets/final3_sfx_820.webm',
    videoMp4: 'https://roboneo-public.meitudata.com/public/roboneo-web-assets/final3_sfx_820.mp4',
    videoTs: 'https://titan-h5.meitu.com/roboneo/assets/overseahome-feature-video3.ts',
    poster: 'https://roboneo-public.meitudata.com/public/roboneo-web-assets/final3_sfx_820_poster.jpg',
  },
];

function FeatureCard({ section, isJSMpegLoaded }: { section: FeatureSection; isJSMpegLoaded: boolean; }) {
  const isMobile = useIsMobile();
  const [showVideo, setShowVideo] = useState(false);
  const [videoCanPlayThrough, setVideoCanPlayThrough] = useState(false);
  const isImageLeft = section.imagePosition === 'left';
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasVideoRef = useRef<HTMLCanvasElement>(null);
  const playerRef = useRef<any>(null);
  const videoContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isJSMpegLoaded || !canvasVideoRef.current || !section.videoTs) return;

    const { JSMpeg } = window as any;
    if (!JSMpeg) return;

    playerRef.current = new JSMpeg.Player(section.videoTs, {
      canvas: canvasVideoRef.current,
      autoplay: false,
      loop: true,
      audio: false,
    });

    return () => {
      playerRef.current?.destroy?.();
    };
  }, [isJSMpegLoaded, section.videoTs]);

  useEffect(() => {
    if (!videoContainerRef.current) return () => {};

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          playerRef.current?.play();
          setShowVideo(true);
          observer.unobserve(videoContainerRef.current as Element);
          observer.disconnect();
        }
      },
      { threshold: 0.8 },
    );

    observer.observe(videoContainerRef.current);
    return () => observer.disconnect();
  }, []);

  const canPlayThrough = () => {
    setVideoCanPlayThrough(true);
  };

  return (
    <section className={styles.sectionContainer}>
      <div
        className={`${styles.sectionContent} ${isImageLeft ? styles.imageLeft : styles.imageRight}`}
      >
        <article className={styles.contentWrapper}>
          <header className={styles.titleSection}>
            <h2 className={styles.title}>{section.title}</h2>
          </header>
          <ul className={styles.featuresList}>
            {section.features.map((feature) => (
              <li key={feature.id} className={styles.featureItem}>
                <div className={styles.featureHeader}>
                  <span className={styles.bullet} aria-hidden="true" />
                  <h3 className={styles.featureTitle}>{feature.title}</h3>
                </div>
                <p className={styles.featureDescription}>{feature.description}</p>
              </li>
            ))}
          </ul>
        </article>
        <div className={styles.videoContainer} ref={videoContainerRef}>

          {isMobile && section.videoTs && (
          <canvas ref={canvasVideoRef} className={styles.video} aria-label={`${section.title} illustration`} />
          )}

          { !isMobile && (
            <img src={handleImageUrl(section.poster)} alt={section.title} className={styles.videoPoster} style={{ opacity: videoCanPlayThrough ? 0 : 1 }} />
          ) }

          { !isMobile && section.videoWebm && showVideo && (
          <video ref={videoRef} className={styles.video} autoPlay loop muted playsInline poster={handleImageUrl(section.poster)} style={{ opacity: videoCanPlayThrough ? 1 : 0 }} onCanPlayThrough={canPlayThrough}>
            <source src={section.videoWebm} type="video/webm" />
            {section.videoMp4 && <source src={section.videoMp4} type="video/mp4" />}
          </video>
          )}

        </div>

      </div>
    </section>
  );
}

export default function FeatureShowcase({ isJSMpegLoaded }: { isJSMpegLoaded: boolean; }) {
  return (
    <div className={styles.container} role="region" aria-label="Feature showcase">
      {featureSections.map((section) => (
        <FeatureCard key={section.id} section={section} isJSMpegLoaded={isJSMpegLoaded} />
      ))}
    </div>
  );
}
